/**
 * 欢迎礼包 / 体验金签名领取
 * challenge + personal_sign（EOA 或 Privy 嵌入式钱包）
 */
import request from '@/utils/request';
import {
  getPrivySessionUser,
  isPrivyCustodialLogin,
  signMessageWithPrivyWallet,
} from '@/utils/privy';
import { getWalletSigningProvider } from '@/utils/wallet-provider';
import { generateBotProof } from '@/utils/botDetector';

function assertChallengeAddress(signedAddress, expectedAddress) {
  const got = String(signedAddress || '')
    .trim()
    .toLowerCase();
  const want = String(expectedAddress || '')
    .trim()
    .toLowerCase();
  if (want && got && got !== want) {
    throw new Error('signed address mismatch with challenge wallet');
  }
}

/**
 * @param {{ purpose?: 'POINTS'|'TRIAL', amount?: number|string }} [params]
 */
export function getWelcomePackChallenge(params = {}) {
  const query = {};
  if (params.purpose) query.purpose = params.purpose;
  if (params.amount != null && params.amount !== '') {
    query.amount = String(params.amount);
  }
  return request.get('/liyuan-trade/wallet/welcome-pack/challenge', query);
}

export function getWelcomePackStatus() {
  return request.get('/liyuan-trade/wallet/welcome-pack/status');
}

/**
 * @param {{ address: string, message: string, signature: string, botProof?: string }} body
 */
export function claimWelcomePack(body) {
  return request.post('/liyuan-trade/wallet/welcome-pack/claim', {
    address: body?.address,
    message: body?.message,
    signature: body?.signature,
    botProof: body?.botProof,
  });
}

/**
 * 对 challenge.message 做 personal_sign（Privy 托管优先，否则当前 EOA provider）
 * @param {string} message
 * @param {string} [expectedAddress]
 * @returns {Promise<{ address: string, signature: string, message: string }>}
 */
export async function signRewardClaimMessage(message, expectedAddress = '') {
  const text = String(message || '').trim();
  if (!text) throw new Error('empty reward claim message');

  if (isPrivyCustodialLogin()) {
    const privyUser = await getPrivySessionUser();
    if (!privyUser) {
      throw new Error('Privy session missing for reward claim');
    }
    const signed = await signMessageWithPrivyWallet(privyUser, text);
    const address = String(signed.walletAddress || '').trim();
    assertChallengeAddress(address, expectedAddress);
    return {
      address,
      signature: String(signed.signature || '').trim(),
      message: text,
    };
  }

  const provider = await getWalletSigningProvider();
  if (!provider?.request) {
    throw new Error('wallet provider unavailable for reward claim signature');
  }
  const accounts = await provider.request({ method: 'eth_accounts' });
  let address = String(expectedAddress || accounts?.[0] || '').trim();
  if (!address) {
    const req = await provider.request({ method: 'eth_requestAccounts' });
    address = String(req?.[0] || '').trim();
  }
  if (!address) throw new Error('no wallet address for reward claim');
  assertChallengeAddress(address, expectedAddress);
  const signature = await provider.request({
    method: 'personal_sign',
    params: [text, address],
  });
  return {
    address,
    signature: String(signature || '').trim(),
    message: text,
  };
}

let welcomePackClaimInFlight = null;

/**
 * @typedef {{ skipped: 'nothing_pending'|'no_address'|'soft_fail' }} WelcomePackSkip
 * @typedef {{ grantedKinds?: string[], points?: string|number }} WelcomePackClaimOk
 */

/**
 * 若有待领种子积分：拉 challenge → personal_sign → claim。
 * 无 pending / 无交易地址时静默跳过（不弹错）。
 * @returns {Promise<WelcomePackClaimOk|WelcomePackSkip>}
 */
export async function claimPendingWelcomePackIfAny() {
  if (welcomePackClaimInFlight) return welcomePackClaimInFlight;
  welcomePackClaimInFlight = (async () => {
    try {
      const status = await getWelcomePackStatus();
      if (!status?.hasPending) return { skipped: 'nothing_pending' };
      const challenge = await getWelcomePackChallenge({ purpose: 'POINTS' });
      if (!challenge?.message || !challenge?.address) {
        return { skipped: 'no_address' };
      }
      const signed = await signRewardClaimMessage(challenge.message, challenge.address);
      let botProof = '';
      try {
        botProof = await generateBotProof('welcome_pack_claim');
      } catch (err) {
        console.warn('[welcome-pack] generateBotProof error', err);
      }
      return await claimWelcomePack({
        ...signed,
        botProof,
      });
    } catch (err) {
      const msg = String(err?.message || err || '');
      // 交易地址尚未可见：可退避重试
      if (/no_trade_address|先绑定|reward\.no_trade_address/i.test(msg)) {
        return { skipped: 'no_address' };
      }
      // 无钱包 / 用户拒签 / 暂无待领：不打断主流程
      if (
        /empty reward|provider unavailable|User rejected|denied|nothing_pending|暂无待领/i.test(msg)
      ) {
        return { skipped: 'soft_fail' };
      }
      console.warn('[welcome-pack] claimPendingWelcomePackIfAny failed', err);
      return { skipped: 'soft_fail' };
    } finally {
      welcomePackClaimInFlight = null;
    }
  })();
  return welcomePackClaimInFlight;
}

/**
 * 托管会话（EMAIL/GOOGLE）在交易地址就绪后：带短退避静默签领 PENDING 种子积分。
 * EOA 直接跳过（避免无感路径弹出小狐狸）；失败吞掉，不打断充值主流程。
 * @param {{ delaysMs?: number[] }} [opts]
 * @returns {Promise<WelcomePackClaimOk|WelcomePackSkip|null>}
 */
export async function claimPendingWelcomePackAfterCustodialReady(opts = {}) {
  if (!isPrivyCustodialLogin()) return null;

  const delays = Array.isArray(opts.delaysMs) && opts.delaysMs.length ? opts.delaysMs : [0, 600, 1600];
  let last = null;
  for (let i = 0; i < delays.length; i += 1) {
    if (delays[i] > 0) {
      await new Promise((r) => setTimeout(r, delays[i]));
    }
    last = await claimPendingWelcomePackIfAny();
    if (last?.skipped === 'nothing_pending') return last;
    // 成功响应（含 grantedKinds 为空的幂等结清）→ 结束，勿再签
    if (last && !last.skipped) {
      if (last.grantedKinds?.length) {
        console.info('[welcome-pack] custodial seed claimed', {
          grantedKinds: last.grantedKinds,
        });
      }
      return last;
    }
    // no_address / soft_fail：地址或 Privy iframe 可能尚未就绪，继续退避
  }
  return last;
}
